import { getUtmSource } from 'providers/profile.provider';
import moment, { Moment } from 'moment';
import { isAnyEstimuloProduct } from 'utils/product-utils';
import { AMBEV, BNDES, SEBRAE } from './product-constants';

const DAYS_FOR_PROPOSAL_TO_EXPIRE = 30;
const ESTIMULO_2020 = 'estimulo2020';
const ESTIMULO_2020_RECEIVABLES = 'estimulo2020_recebiveis';
const ESTIMULO_2020_BR = 'estimulo2020_br';
const ESTIMULO_2020_RS = 'estimulo2020_rs';
const CAPITAL_GIRO = 'capital-giro';

const BIZCONTA_APP = 'bizconta-app';

const PRE_APPROVED_PRODUCTS_NAME = [AMBEV];
const PRODUCTS_WITHOUT_PARTNERS_HEADER_NAME = [AMBEV, SEBRAE, BNDES];
const PRODUCTS_WITH_RECEIVABLES = [ESTIMULO_2020_RECEIVABLES];
const PRODUCTS_ESTIMULO = [
    ESTIMULO_2020,
    ESTIMULO_2020_RECEIVABLES,
    ESTIMULO_2020_BR,
    ESTIMULO_2020_RS,
];

const GRACE_PERIOD = 30;
const PAYMENT_THRESHOLD = 59;
const AVAILABLE_PAYMENT_DAYS = [2, 3, 6, 9, 12, 30].sort((a, b) => a - b);

const getPaymentDateOptions = (date: Moment): Moment[] => {
    const start = date.clone().add(GRACE_PERIOD, 'days');
    const threshold = date.clone().add(PAYMENT_THRESHOLD, 'days');

    return pushPaymentDate(getNextPossiblePaymentDate(start, true), threshold);
};

const pushPaymentDate = (
    date: Moment,
    threshold: Moment,
    dates: Moment[] = []
): Moment[] => {
    const canAddNextDate =
        date.isSameOrBefore(threshold) &&
        dates.length < AVAILABLE_PAYMENT_DAYS.length;
    if (canAddNextDate) {
        dates.push(date);
        return pushPaymentDate(
            getNextPossiblePaymentDate(date),
            threshold,
            dates
        );
    }
    return dates;
};

const getNextPossiblePaymentDate = (
    date: Moment,
    isFirst?: boolean
): Moment => {
    const clone = date.clone();
    const day = getClosestPaymentDay(clone.date(), isFirst);
    return day
        ? clone.set('date', day)
        : clone.add(1, 'months').set('date', AVAILABLE_PAYMENT_DAYS[0]);
};

const getClosestPaymentDay = (goal: number, exact: boolean): number =>
    AVAILABLE_PAYMENT_DAYS.find((n: number) => (exact ? n >= goal : n > goal));

const CONFIG = {
    MONTHLY_REVENUE: {
        MAX: () => 9999999,
        MIN: () => (getUtmSource() === AMBEV ? 1000 : 10000),
    },
    COMISSION: {
        FIXED_VALUE: () => false,
        FIXED_PERCENT: () => {
            if (getUtmSource() === SEBRAE) {
                return 2.5;
            } else if (isAnyEstimuloProduct(getUtmSource())) {
                return 3.0;
            } else {
                return 8.0;
            }
        },
    },
    COUPON: {
        CAN_APPLY: () =>
            !isAnyEstimuloProduct(getUtmSource()) &&
            getUtmSource() !== AMBEV &&
            getUtmSource() !== SEBRAE,
    },
    INFORMED_INCOME: {
        MIN: () => (getUtmSource() === AMBEV ? 1000 : 10000),
    },
    BANK: {
        AVAILABLE_OPTIONS: (banks = []) => {
            if (isAnyEstimuloProduct(getUtmSource())) {
                return [
                    {
                        label: 'Bancos',
                        options: banks.filter((b) => b.mostUsed),
                    },
                    {
                        label: 'Demais',
                        options: banks.filter(
                            (b) => b.code === '77' || b.code === '756'
                        ),
                    },
                ];
            }
            return [
                {
                    label: 'Bancos',
                    options: banks.filter((b) => b.mostUsed),
                },
                {
                    label: 'Demais',
                    options: banks.filter((b) => !b.mostUsed),
                },
            ];
        },
    },
};

const minLoanAmount = (utmSource: string, productBizId: string) => {
    const def = 'default';

    const options = {
        [AMBEV]: 1000,
        [SEBRAE]: 1500,
        [def]: productBizId === BNDES ? 30000 : 5000,
    };

    return options[utmSource] || options[def];
};

const productConfig = (
    productBizId: string,
    maxNumberOfInstallments?: number,
    minNumberOfInstallments?: number
) => {
    return {
        LOAN_AMOUNT: {
            MIN: () => minLoanAmount(getUtmSource(), productBizId),
            MAX: () => (productBizId === BNDES ? 500000 : 400000),
        },
        TENURE: {
            MIN: () => minNumberOfInstallments || 3,
            MAX: () => maxNumberOfInstallments || 24,
            DEFAULT_OPTIONS: [6, 9, 12, 15, 18, 21, 24],
        },
        PAYMENT_DAY: {
            DEFAULT: 9,
            AVAILABLE_OPTIONS: () => {
                if (isAnyEstimuloProduct(getUtmSource())) {
                    const allowedDays = [2, 3, 6, 9, 12, 20, 30];
                    const paymentDay = moment().add(30, 'd');

                    // check if paymentDay is allowed, add one until it is
                    while (!allowedDays.includes(paymentDay.date())) {
                        paymentDay.add(1, 'd');
                    }
                    return [
                        {
                            label: paymentDay.format('[Dia] DD'),
                            value: paymentDay.format('YYYY-MM-DD'),
                        },
                    ];
                }
                if (getUtmSource() === SEBRAE) {
                    const paymentDay = moment().add(6, 'M');
                    return [
                        {
                            label: paymentDay.format('[Dia] DD'),
                            value: paymentDay.format('YYYY-MM-DD'),
                        },
                    ];
                }
                if (productBizId === BNDES) {
                    const paymentDay = moment().add(60, 'd');
                    return [
                        {
                            label: paymentDay.format('[Dia] DD'),
                            value: paymentDay.format('YYYY-MM-DD'),
                        },
                    ];
                }
                const possiblePaymentDay = moment();

                return getPaymentDateOptions(possiblePaymentDay).map(
                    (paymentDate) => ({
                        label: paymentDate.format('[Dia] DD'),
                        value: paymentDate.format('YYYY-MM-DD'),
                    })
                );
            },
        },
    };
};

export {
    DAYS_FOR_PROPOSAL_TO_EXPIRE,
    CONFIG,
    productConfig,
    CAPITAL_GIRO,
    ESTIMULO_2020,
    ESTIMULO_2020_RECEIVABLES,
    ESTIMULO_2020_BR,
    ESTIMULO_2020_RS,
    PRODUCTS_ESTIMULO,
    PRODUCTS_WITH_RECEIVABLES,
    BIZCONTA_APP,
    PRE_APPROVED_PRODUCTS_NAME,
    PRODUCTS_WITHOUT_PARTNERS_HEADER_NAME,
};
